import {
  Bookmark,
  BookMarked,
  CheckCircle2,
  CheckSquare,
  Compass,
  Expand,
  FileText,
  GitBranch,
  Highlighter,
  Layers,
  LibraryBig,
  Lightbulb,
  MessageCircle,
  Podcast,
  Scale,
  Target,
  type LucideIcon
} from 'lucide-react';
import type { FeatureKey, Scope } from '../types';

export type { Scope };

export type FeatureMeta = {
  key: FeatureKey;
  label: string;
  description: string;
  icon: LucideIcon;
  tint: string;
  /**
   * Scopes this activity supports. Empty/undefined = doesn't appear in
   * the scoped grid (used for `threads`, which lives in the composer).
   */
  scopes?: Scope[];
  beta?: boolean;
};

// Order is driven by real usage data: most-launched activities first so
// they sit in the first rows of the grid (within reach without scroll).
// Chat dominates traffic but lives in the bottom composer, not in the grid.
//
// Tints carry meaning — same color = same "kind of use" — so the grid
// reads as a free taxonomy:
//   rose  → practice / recall  (the bulk of usage, on top)
//   cyan  → assimilate content (also heavy usage)
//   blue  → orient yourself in the course (context, medium usage)
//   amber → apply / go deeper
//   slate → static references (lookups, low usage)
export const FEATURE_CATALOG: FeatureMeta[] = [
  {
    key: 'remember',
    label: 'Recordar',
    description: 'Lo esencial para fijar en memoria',
    icon: Bookmark,
    tint: 'var(--tint-rose)',
    scopes: ['module']
  },
  {
    key: 'quiz',
    label: 'Cuestionario',
    description: 'Pon a prueba lo que has aprendido',
    icon: CheckCircle2,
    tint: 'var(--tint-rose)',
    scopes: ['module', 'section', 'course']
  },
  {
    key: 'summary',
    label: 'Resumen',
    description: 'Las ideas clave en texto o audio',
    icon: FileText,
    tint: 'var(--tint-cyan)',
    scopes: ['module', 'section', 'course']
  },
  {
    key: 'explain',
    label: 'Explicar',
    description: 'Detallada o sencilla, tú eliges',
    icon: Lightbulb,
    tint: 'var(--tint-cyan)',
    scopes: ['module']
  },
  {
    key: 'podcasts',
    label: 'Podcast',
    description: 'Escucha la lección como un audio',
    icon: Podcast,
    tint: 'var(--tint-cyan)',
    scopes: ['section']
  },
  {
    key: 'diagram',
    label: 'Diagrama',
    description: 'Visualiza la estructura del tema',
    icon: GitBranch,
    tint: 'var(--tint-cyan)',
    scopes: ['module']
  },
  {
    key: 'flashcards',
    label: 'Tarjetas',
    description: 'Memoriza con un mazo interactivo',
    icon: Layers,
    tint: 'var(--tint-rose)',
    scopes: ['module']
  },
  {
    key: 'discover',
    label: 'Descubrir el curso',
    description: 'Visión general, itinerario y resultados',
    icon: Compass,
    tint: 'var(--tint-blue)',
    scopes: ['course']
  },
  {
    key: 'goals',
    label: 'Objetivos',
    description: 'Qué deberías saber al acabar',
    icon: Target,
    tint: 'var(--tint-blue)',
    scopes: ['course']
  },
  {
    key: 'applications',
    label: 'Aplicaciones prácticas',
    description: 'Aplicación a problemas reales',
    icon: CheckSquare,
    tint: 'var(--tint-amber)',
    scopes: ['course']
  },
  {
    key: 'expand',
    label: 'Ampliar',
    description: 'Profundiza más allá del temario',
    icon: Expand,
    tint: 'var(--tint-amber)',
    scopes: ['module']
  },
  {
    key: 'underline',
    label: 'Subrayar',
    description: 'Las frases clave resaltadas',
    icon: Highlighter,
    tint: 'var(--tint-cyan)',
    scopes: ['module']
  },
  {
    key: 'glossary',
    label: 'Glosario',
    description: 'Términos clave del módulo',
    icon: BookMarked,
    tint: 'var(--tint-slate)',
    scopes: ['course']
  },
  {
    key: 'bibliography',
    label: 'Bibliografía',
    description: 'Fuentes y lecturas recomendadas',
    icon: LibraryBig,
    tint: 'var(--tint-slate)',
    scopes: ['course']
  },
  {
    key: 'regulation',
    label: 'Normativa',
    description: 'Marco legal relacionado',
    icon: Scale,
    tint: 'var(--tint-slate)',
    scopes: ['course']
  },
  // Chat lives in the composer at the bottom of HomeView, not in the grid,
  // but the entry stays so meta lookup (back-button title, etc.) keeps working.
  {
    key: 'threads',
    label: 'Chat',
    description: 'Pregunta lo que quieras sobre el módulo',
    icon: MessageCircle,
    tint: 'var(--tint-blue)'
  }
];

export const FEATURE_BY_KEY: Record<FeatureKey, FeatureMeta> = Object.fromEntries(
  FEATURE_CATALOG.map((f) => [f.key, f])
) as Record<FeatureKey, FeatureMeta>;
